//
//  Setup.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 13, 2026
//  Author: Bob Keathley
//
//  Tenant console setup page for the emailed setup or reset link (spec 6i).
//  Sets the password from the single-use code and adopts the returned session.
//  A bad, expired or used code shows one indistinguishable 401 message.
//
import { useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import type { SetupRequest } from '@shared/api/types'
import { auth, type User } from '@shared/auth'
import BrandMark from '@shared/components/BrandMark'
import PasswordInput from '@shared/components/PasswordInput'
import { navigate } from '@shared/router'

// `#/setup?code=…` -- the emailed setup or reset link (§6i). Redeeming the single-use code sets the password
// and returns a session; we adopt it and land on Fleet. A bad/expired/used code is one indistinguishable 401.
const MIN = 12

export default function Setup({ code }: { code: string }) {
  const [pwLen, setPwLen] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // Read from the form so a browser-autofilled value counts, then say which check failed.
    const f = new FormData(e.currentTarget)
    const password = String(f.get('password') ?? '')
    const confirm = String(f.get('confirm') ?? '')
    if (!code) { setError('This page needs the setup link from your email.'); return }
    if (password.length < MIN) { setError(`Your new password needs at least ${MIN} characters.`); return }
    if (password !== confirm) { setError("The two passwords don't match."); return }
    setBusy(true); setError(null)
    const body: SetupRequest = { code, password }
    const r = await api<{ token: string; expires_at: number; user: User }>('/auth/setup', { method: 'POST', body: JSON.stringify(body) })
    if (!isOk(r)) {
      setBusy(false)
      setError(r.error.code === 'unauthorized' ? 'This setup link is invalid, expired or already used. Ask your administrator for a new one, or use “Forgot password?” on the sign-in page.' : r.error.message)
      return
    }
    const a = await auth.adopt(r.data.token, r.data.expires_at)
    setBusy(false)
    if (!a.ok) { setError(a.error); return }
    navigate('/fleet')
  }

  return (
    <div className="login">
      <form className="card" onSubmit={submit} noValidate aria-label="Set password">
        <BrandMark variant="mark" size={88} />
        <p className="brand"><b>Signet</b> Console</p>
        <p className="hint">Set a new password to finish setting up your account.</p>
        {!code && <p className="err" role="alert">This page needs the setup link from your email.</p>}
        <label htmlFor="pw">Password ({MIN}+ characters)</label>
        <PasswordInput id="pw" name="password" autoComplete="new-password" required autoFocus onChange={(e) => setPwLen(e.target.value.length)} />
        <p className="hint" aria-live="polite">{Math.min(pwLen, MIN)} of {MIN} characters</p>
        <label htmlFor="pw2">Confirm password</label>
        <PasswordInput id="pw2" name="confirm" autoComplete="new-password" required />
        {error && <p className="err" role="alert">{error}</p>}
        <div style={{ marginTop: 20 }}>
          <button className="btn" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Setting password…' : 'Set password and sign in'}
          </button>
        </div>
      </form>
    </div>
  )
}
